import { Link } from 'react-router-dom';

interface Writer {
  id: string;
  display_name: string;
  username: string;
  bio: string | null;
  avatar_url: string | null;
  join_date: string | null;
  story_count: number;
  total_likes: number;
  total_reads: number;
}

interface WriterCardProps {
  writer: Writer;
  index: number;
}

const formatNumber = (n: number) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const WriterCard = ({ writer, index }: WriterCardProps) => {
  const avatarSrc =
    writer.avatar_url ||
    `https://public.readdy.ai/ai/img_res/88518b9d95176fcfeaed3b2aa3175ff8.jpg${writer.id}&orientation=squarish`;

  return (
    <Link
      to={`/profile/writer/${writer.id}`}
      className="group bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-red-900/20 cursor-pointer flex flex-col"
      style={{ animation: `fadeInUp 0.5s ease-out ${index * 0.06}s both` }}
    >
      {/* Banner */}
      <div className="relative h-28 overflow-hidden shrink-0">
        <img
          src={`https://readdy.ai/api/search-image?query=dark%20gothic%20horror%20atmospheric%20abstract%20background%20deep%20crimson%20black%20misty%20fog%20dramatic%20cinematic%20texture%20minimal&width=800&height=200&seq=writer-banner-${writer.id}&orientation=landscape`}
          alt=""
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-[#0f0f0f]" />
      </div>

      {/* Avatar */}
      <div className="px-6 -mt-10 relative z-10">
        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-[#0f0f0f] group-hover:border-red-900/50 transition-colors duration-300 shrink-0">
          <img
            src={avatarSrc}
            alt={writer.display_name || writer.username}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Info */}
      <div className="px-6 pt-3 pb-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-lg font-bold text-white font-['Cinzel'] group-hover:text-red-500 transition-colors leading-tight line-clamp-1">
            {writer.display_name || writer.username}
          </h3>
          <span className="shrink-0 px-2 py-0.5 bg-red-900/20 border border-red-900/40 text-red-400 text-xs rounded font-['Crimson_Text'] whitespace-nowrap">
            <i className="ri-quill-pen-line mr-1" />
            Writer
          </span>
        </div>

        <p className="text-gray-500 text-xs font-['Crimson_Text'] mb-3">
          <i className="ri-calendar-line mr-1" />
          Joined {formatDate(writer.join_date)}
        </p>

        <p className="text-gray-400 text-sm font-['Crimson_Text'] leading-relaxed line-clamp-2 flex-1 mb-4">
          {writer.bio || 'This author dwells in the shadows, letting their stories speak for them.'}
        </p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 pt-4 border-t border-red-900/20">
          <div className="text-center">
            <div className="text-base font-bold text-white font-['Cinzel']">{writer.story_count}</div>
            <div className="text-xs text-gray-500 font-['Crimson_Text']">Stories</div>
          </div>
          <div className="text-center border-x border-red-900/20">
            <div className="text-base font-bold text-white font-['Cinzel']">{formatNumber(writer.total_reads)}</div>
            <div className="text-xs text-gray-500 font-['Crimson_Text']">Reads</div>
          </div>
          <div className="text-center">
            <div className="text-base font-bold text-white font-['Cinzel']">{formatNumber(writer.total_likes)}</div>
            <div className="text-xs text-gray-500 font-['Crimson_Text']">Likes</div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default WriterCard;
